import type { ReactElement } from 'react';
import type { ExtensionRiskEstimate } from '@kirin/api-client/contracts/extensions';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { RiskEstimateCell } from '@/components/shared/extensions/RiskEstimateCell';
import { riskEstimateLabel } from '@/components/shared/extensions/agentmesh-risk-labels';

interface AgentmeshRiskDetailsProps {
  riskEstimate: ExtensionRiskEstimate | undefined;
  /** Built-ins are never scanned (KIR-4726). */
  isBuiltIn?: boolean;
}

/**
 * Expanded-row section showing the full AgentMesh assessment for an
 * extension (KIR-1928): overall estimate, assessment reasons, and code
 * evidence snippets. Renders a "Not assessed" empty state when AgentMesh has
 * no record for the extension.
 */
export function AgentmeshRiskDetails({
  riskEstimate,
  isBuiltIn = false,
}: AgentmeshRiskDetailsProps): ReactElement {
  const label = riskEstimateLabel(riskEstimate);

  return (
    <div className="space-y-3 px-4 py-3" data-testid="agentmesh-risk-details">
      <div className="flex items-center gap-2">
        <h4 className="text-sm font-semibold">Risk Estimate</h4>
        {/* The paragraph below explains a label-less state. */}
        <RiskEstimateCell
          riskEstimate={riskEstimate}
          isBuiltIn={isBuiltIn}
          describe={false}
        />
        {riskEstimate && label !== null ? (
          <span className="text-xs text-muted-foreground">
            Assessed <RelativeTimeWithTooltip date={riskEstimate.checkedAt} />
          </span>
        ) : null}
        {riskEstimate?.agentmeshUrl ? (
          <a
            href={riskEstimate.agentmeshUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline"
          >
            View in AgentMesh ↗
          </a>
        ) : null}
      </div>

      {label === null && isBuiltIn ? (
        <p className="text-sm text-muted-foreground">
          This extension ships inside the IDE, so AgentMesh does not scan it.
        </p>
      ) : label === null ? (
        <p className="text-sm text-muted-foreground">
          No AgentMesh assessment is available for this extension yet.
        </p>
      ) : (
        <>
          {riskEstimate && riskEstimate.reasons.length > 0 ? (
            <div>
              <h5 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Assessment
              </h5>
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {riskEstimate.reasons.map((reason, idx) => (
                  <li key={`${idx}-${reason}`}>{reason}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {riskEstimate && riskEstimate.evidence.length > 0 ? (
            <div>
              <h5 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Evidence
              </h5>
              <div className="space-y-2">
                {riskEstimate.evidence.map((item, idx) => (
                  <div key={`${idx}-${item.filePath}`}>
                    <div className="text-xs text-muted-foreground">
                      {item.filePath}
                    </div>
                    <pre className="mt-0.5 overflow-x-auto rounded bg-muted p-2 text-xs">
                      {item.snippet}
                    </pre>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

AgentmeshRiskDetails.displayName = 'AgentmeshRiskDetails';
